import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import { ReactComponent as Next } from 'assets/icons/chevronDown.svg'
import { ReactComponent as Prev } from 'assets/icons/chevronUp.svg'
import classNames from 'classnames/bind'
import { useWindowSize } from 'shared/hooks'
import { sleep } from 'shared/utils/sleep'

import { SlideItem } from './SlideItem'
import classes from './VerticalCarousel.module.scss'

export type SliderItem = {
  child?: ReactNode
  [key: string]: any
}

type Props = {
  activeSlideIdx?: number
  sliderItems: SliderItem[]
  isFull?: boolean
  onChange: (sliderIndex: number) => void
}

const cx = classNames.bind(classes)

export const VerticalCarousel = ({
  activeSlideIdx = 0,
  sliderItems,
  isFull,
  onChange,
}: Props): JSX.Element => {
  const listRef = useRef<HTMLUListElement>(null)
  const length = sliderItems.length
  const _items = [...sliderItems, ...sliderItems]
  const jump = 1

  const [items, setItems] = useState(Array.from(Array(_items.length).keys()))
  const [isTicking, setIsTicking] = useState(false)
  const [active, setActive] = useState(activeSlideIdx)

  const { isLargeDesktop, isDesktop } = useWindowSize()
  const bigLength = items.length

  const btnClick = (dir: 'next' | 'prev') => {
    if (!isTicking) {
      setIsTicking(true)
      turnCarousel(dir)
    }
  }

  const turnCarousel = (dir: 'next' | 'prev') => {
    return setItems(prev =>
      dir === 'prev'
        ? prev.map((_, i) => prev[(i + jump) % bigLength])
        : prev.map((_, i) => prev[(i - jump + bigLength) % bigLength])
    )
  }

  const renderBtn = (dir: 'next' | 'prev') => {
    const isPrev = dir === 'prev'
    const _classes = cx(['button_reset', classes['carousel__button']], {
      [classes.prev]: isPrev,
      [classes.next]: !isPrev,
    })
    const icon = isPrev ? <Prev /> : <Next />

    return (
      <button className={_classes} onClick={() => btnClick(dir)}>
        {icon}
      </button>
    )
  }

  useEffect(() => {
    if (listRef?.current) {
      listRef?.current.style.setProperty('--slide-count', `${length}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isTicking) sleep(300).then(() => setIsTicking(false))
  }, [isTicking])

  useEffect(() => {
    const _i = (length - (items[0] % length)) % length
    onChange(_i)
    setActive(_i)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items])

  useEffect(() => {
    if (active !== activeSlideIdx) {
      turnCarousel('next')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSlideIdx])

  const changeSlide = (index: number) => {
    if (index !== active) {
      setActive(index)
      onChange(index)
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const renderTablet = () => {
    //Сейчас всего два виде слайдера. Если потребуется заменить isFull на отдельны пропс
    const containerClass = cx([classes.mobile_wrap, { dots: !isFull }])
    return (
      <div className={containerClass}>
        <div className="container">
          <div className={classes.mobile__container}>
            {sliderItems.map((item, itemIndex) => {
              const cn = cx(['mobile__item', { active: active === itemIndex }])
              return (
                <div
                  key={`carouselCount_${itemIndex}`}
                  className={cn}
                  onClick={() => changeSlide(itemIndex)}
                >
                  <span>0{itemIndex + 1}</span>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const renderDesktop = () => {
    const wrapClass = cx(['carousel__wrap', { full: isFull }])
    return (
      <div className={wrapClass}>
        <div className="container">
          <div className={classes['carousel__inner']}>
            {renderBtn('prev')}

            <div className={classes['carousel__container']}>
              <ul ref={listRef} className={classes['carousel__slide-list']}>
                {items.map((pos, i) => {
                  const prevIdx = active - 1 >= 0 ? active - 1 : length - 1

                  return (
                    <SlideItem
                      key={i}
                      idx={i}
                      pos={pos}
                      length={length}
                      isFull={isFull}
                      onClick={idx => {
                        if (idx === active) {
                          return
                        }

                        btnClick(idx === prevIdx ? 'prev' : 'next')
                      }}
                    />
                  )
                })}
              </ul>
            </div>

            {renderBtn('next')}
          </div>
        </div>
      </div>
    )
  }

  const render = useCallback(
    () => (isLargeDesktop || isDesktop ? renderDesktop() : renderTablet()),
    [isLargeDesktop, isDesktop, renderDesktop, renderTablet]
  )

  return render()
}
